<template>
  <v-img
    :src="currentSrc"
    :alt="alt"
    :width="width"
    :height="height"
    :cover="cover"
    :class="imageClass"
    :style="imageStyle"
    :loading="loading"
    @error="handleImageError"
    @load="handleImageLoad"
    v-bind="$attrs"
    :key="imageKey"
  >
    <template v-slot:placeholder>
      <div class="d-flex align-center justify-center fill-height">
        <v-progress-circular
          color="grey-lighten-5"
          indeterminate
          size="24"
          :aria-label="t('common.loading_image')"
        ></v-progress-circular>
      </div>
    </template>
  </v-img>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getImageUrl, getOriginalImageUrl } from '@/utils/images'

const { t } = useI18n()

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  alt: {
    type: String,
    default: ''
  },
  width: {
    type: [String, Number],
    default: undefined
  },
  height: {
    type: [String, Number],
    default: undefined
  },
  cover: {
    type: Boolean,
    default: false
  },
  useThumbnail: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: 'thumb',
    validator: (value) => ['thumb', 'card'].includes(value)
  },
  imageClass: {
    type: String,
    default: ''
  },
  imageStyle: {
    type: Object,
    default: () => ({})
  },
  /** 'lazy' para below-the-fold, 'eager' para above-the-fold (hero, avatar) */
  loading: {
    type: String,
    default: 'lazy',
    validator: (v) => ['lazy', 'eager'].includes(v)
  }
})

const hasError = ref(false)
const isLoading = ref(true)
const currentSrc = ref('')

// Calcular la URL de la miniatura
const thumbnailUrl = computed(() => {
  if (!props.src) return ''
  if (!props.useThumbnail) return getOriginalImageUrl(props.src)
  return getImageUrl(props.src, true, false, props.size)
})

// Calcular la URL original
const originalUrl = computed(() => {
  if (!props.src) return ''
  return getOriginalImageUrl(props.src)
})

// Key para forzar re-render cuando cambia la fuente
const imageKey = computed(() => {
  return `${currentSrc.value}-${hasError.value}`
})

// Inicializar la URL actual
function updateCurrentSrc() {
  if (!props.src) {
    currentSrc.value = ''
    return
  }
  
  // Si hay error y estamos intentando la miniatura, usar la original
  if (hasError.value && props.useThumbnail) {
    currentSrc.value = originalUrl.value
    return
  }
  
  // Si no queremos usar miniatura, usar directamente la original
  if (!props.useThumbnail) {
    currentSrc.value = originalUrl.value
    return
  }
  
  // Por defecto, intentar la miniatura
  currentSrc.value = thumbnailUrl.value
}

// Inicializar
updateCurrentSrc()

// Manejar error de carga de imagen
function handleImageError(event) {
  // Si la miniatura falla y aún no hemos intentado la original, cambiar a original
  if (props.useThumbnail && !hasError.value && currentSrc.value === thumbnailUrl.value) {
    hasError.value = true
    updateCurrentSrc()
  } else {
    // Si ya intentamos ambas, mantener el error
    isLoading.value = false
  }
}

// Manejar carga exitosa
function handleImageLoad() {
  isLoading.value = false
  hasError.value = false
}

// Resetear estado cuando cambia la fuente
watch(() => props.src, () => {
  hasError.value = false
  isLoading.value = true
  updateCurrentSrc()
})

// Resetear estado cuando cambia useThumbnail
watch(() => props.useThumbnail, () => {
  hasError.value = false
  isLoading.value = true
  updateCurrentSrc()
})

// Actualizar cuando cambian las URLs calculadas
watch([thumbnailUrl, originalUrl], () => {
  if (!hasError.value) {
    updateCurrentSrc()
  }
})
</script>

<style scoped>
/* Estilos adicionales si son necesarios */
</style>

